import { useMemo } from "react";
import { Mail, User } from "lucide-react";
import { FieldWrapper } from "@/components/ui/FieldWrapper";
import DatePicker from "@/components/ui/DatePicker";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import type { FormErrors, MandateForm, SetField } from "../types";
import PrefixInput from "./PrefixInput";

const STATE_OPTIONS = [
  "Andhra Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Gujarat",
  "Haryana",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Tamil Nadu",
  "Telangana",
  "Uttar Pradesh",
  "West Bengal",
].map((s) => ({ value: s, label: s }));

function formatAadhaar(v: string) {
  const digits = v.replace(/\D/g, "").slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
}

interface Props {
  form: MandateForm;
  errors: FormErrors;
  setField: SetField;
}

export default function CustomerStep({ form, errors, setField }: Props) {
  const maxDob = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d;
  }, []);

  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Customer Details</h3>
      <p className="text-[13px] text-ink-secondary">
        Details of the customer who will authorise this mandate
      </p>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <Input
          label="Customer Name"
          required
          error={errors.customerName}
          placeholder="Enter full name as per bank records"
          id="customerName"
          prefixIcon={<User className="h-4 w-4" />}
          value={form.customerName}
          onChange={(e) => setField("customerName", e.target.value)}
        />

        <FieldWrapper
          label="Mobile Number"
          required
          error={errors.customerMobile}
          htmlFor="customerMobile"
        >
          <PrefixInput
            prefix="+91"
            id="customerMobile"
            inputMode="numeric"
            maxLength={10}
            placeholder="Enter 10-digit mobile number"
            error={!!errors.customerMobile}
            value={form.customerMobile}
            onChange={(e) => setField("customerMobile", e.target.value.replace(/\D/g, ""))}
          />
        </FieldWrapper>

        <Input
          label="Email Address"
          required
          type="email"
          error={errors.customerEmail}
          placeholder="customer@email.com"
          id="customerEmail"
          prefixIcon={<Mail className="h-4 w-4" />}
          helper="Authorisation link will also be sent to this email"
          value={form.customerEmail}
          onChange={(e) => setField("customerEmail", e.target.value)}
        />

        <FieldWrapper
          label="Date of Birth"
          required
          error={errors.dob}
          htmlFor="dob"
          helper="Customer must be at least 18 years of age"
        >
          <DatePicker
            id="dob"
            value={form.dob}
            placeholder="Select date of birth"
            maxDate={maxDob}
            onChange={(v) => setField("dob", v)}
          />
        </FieldWrapper>

        <Input
          label="Customer PAN"
          required
          error={errors.customerPan}
          id="customerPan"
          maxLength={10}
          placeholder="ABCDE1234F"
          helper="Format: ABCDE1234F"
          value={form.customerPan}
          onChange={(e) =>
            setField("customerPan", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
          }
        />

        <FieldWrapper
          label="Aadhaar Number"
          required
          error={errors.aadhaar}
          htmlFor="aadhaar"
          helper="12-digit UID as per your records"
        >
          <input
            id="aadhaar"
            inputMode="numeric"
            maxLength={14}
            placeholder="1234 5678 9012"
            value={form.aadhaar}
            onChange={(e) => setField("aadhaar", formatAadhaar(e.target.value))}
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.aadhaar
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>

        <div className="md:col-span-2">
          <Textarea
            label="Residential Address"
            required
            rows={2}
            error={errors.address}
            id="address"
            placeholder="House number, street, area, landmark"
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
          />
        </div>

        <Input
          label="City"
          required
          error={errors.city}
          id="city"
          placeholder="Enter city"
          value={form.city}
          onChange={(e) => setField("city", e.target.value)}
        />

        <Select
          label="State"
          required
          placeholder="Select state"
          options={STATE_OPTIONS}
          error={errors.state}
          id="state"
          value={form.state}
          onChange={(e) => setField("state", e.target.value)}
        />

        <FieldWrapper label="Pincode" required error={errors.pincode} htmlFor="pincode">
          <input
            id="pincode"
            inputMode="numeric"
            maxLength={6}
            placeholder="6-digit pincode"
            value={form.pincode}
            onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, ""))}
            className={`h-[42px] w-full rounded-lg border bg-white px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 ${
              errors.pincode
                ? "border-danger focus:ring-danger/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
            }`}
          />
        </FieldWrapper>
      </div>
    </div>
  );
}